/**
 * `useSelectionColumn` — the preset's cross-page selection wiring: derives the header
 * checkbox state + bulk payload + counts, owns the toggle/select-all/clear handlers, and
 * builds the sticky-pin styles for the model-external leading selection column.
 *
 * The header state keys off the dedup-consistent `logicalCount`, never a raw page length.
 * During a keepPreviousData refetch the caller passes `staleRows`, which feeds
 * `loadedCount: 0` so the header never implies a select-all over rows about to vanish.
 */
import type { CSSProperties } from 'react'
import { useEvent } from 'react-use-event-hook'
import type { RowData, Table } from '@tanstack/react-table'

import type { DataTableSelection } from './data-table.types'
import type { DataTableSurface } from './primitives/data-table.styles'
import { PINNED_Z_INDEX, pinnedCellSurfaceClass } from './primitives/pinning.styles'
import {
  EMPTY_SELECTION,
  getLoadedRowIds,
  selectAllLoaded,
  selectAllMatching,
  type SelectionModel,
  toggleRow,
  useTableSelection,
  type UseTableSelectionResult,
} from './primitives/selection'

export type UseSelectionColumnOptions<TData extends RowData> = {
  /** The cross-page selection wiring, or `undefined` for no selection column. */
  selection: DataTableSelection | undefined
  table: Table<TData>
  /**
   * keepPreviousData signal: during the stale window the header must not derive from the
   * soon-to-be-replaced rows, so the hook feeds `loadedCount: 0`.
   */
  staleRows: boolean
  /** Dedup-consistent loaded count the header derives from. */
  logicalCount: number
  serverTotal?: number
  hasNextPage?: boolean
  /** The card's surface — the opaque background a pinned selection BODY cell paints. */
  surface: DataTableSurface
  /**
   * The chrome bands' surface — the opaque background a pinned selection cell paints in
   * the header row AND in the summary band (both bands share `headerSurface`).
   */
  headerSurface: DataTableSurface
}

export type SelectionColumn = {
  selectionPinnedStyle: CSSProperties | undefined
  selectionPinnedHeaderStyle: CSSProperties | undefined
  selectionCellClassName: string | undefined
  selectionHeaderClassName: string | undefined
  derivedSelection: UseTableSelectionResult
  handleToggleRow: (id: string, next: boolean) => void
  /**
   * Header checkbox click. Clears when `next` is `false` OR when anything is already
   * selected (DataGridPro parity — an indeterminate click clears, never grows the
   * selection); otherwise selects all. What "all" means depends on
   * `selection.selectAllScope`: `'loaded'` (default) selects the loaded rows only
   * (inclusion), `'allMatching'` selects the whole filtered set directly (exclusion).
   */
  handleToggleAll: (next: boolean) => void
  /** Switch to the exclusion model ("select all N matching"). */
  handleSelectAllMatching: () => void
  handleClearSelection: () => void
}

const SELECTION_PINNED_BODY_STYLE: CSSProperties = {
  position: 'sticky',
  left: 0,
  zIndex: PINNED_Z_INDEX.pinnedBody,
}
const SELECTION_PINNED_HEADER_STYLE: CSSProperties = {
  position: 'sticky',
  left: 0,
  zIndex: PINNED_Z_INDEX.pinnedHeader,
}

export const useSelectionColumn = <TData extends RowData>(
  options: UseSelectionColumnOptions<TData>,
): SelectionColumn => {
  const {
    selection,
    table,
    staleRows,
    logicalCount,
    serverTotal,
    hasNextPage,
    surface,
    headerSurface,
  } = options
  const selectionModel = selection?.model
  const hasSelectionColumn = selection !== undefined

  // The selection column is ALWAYS sticky at `left: 0` whenever a selection model is
  // present — DataGridPro parity: the checkbox gutter never scrolls away under horizontal
  // scroll (browser-QA'd against production). Module-level style objects (stable identity)
  // so a body row's props don't change on unrelated renders.
  const selectionPinnedStyle: CSSProperties | undefined = hasSelectionColumn
    ? SELECTION_PINNED_BODY_STYLE
    : undefined
  const selectionPinnedHeaderStyle: CSSProperties | undefined = hasSelectionColumn
    ? SELECTION_PINNED_HEADER_STYLE
    : undefined
  // Opaque surface for a pinned cell so scrolled content can't bleed through. Each plane
  // paints ITS OWN surface, exactly as `getPinnedCellProps` does for the model-backed
  // columns beside it.
  const selectionCellClassName = hasSelectionColumn ? pinnedCellSurfaceClass(surface) : undefined
  const selectionHeaderClassName = hasSelectionColumn
    ? pinnedCellSurfaceClass(headerSurface)
    : undefined

  // Header state + bulk payload derive from the model and dedup-consistent `logicalCount`
  // — never a raw page length (a duplicate id double-counts) nor the loaded row model (an
  // unloaded selected id would drop from a bulk action). `loadedCount` gates to 0 during a
  // keepPreviousData refetch.
  const derivedSelection = useTableSelection({
    model: selectionModel ?? EMPTY_SELECTION,
    loadedCount: staleRows ? 0 : logicalCount,
    serverTotal,
    hasNextPage,
  })

  // Event-time transitions (never during render) → `useEvent`; each reads the live model /
  // table instance at call time, never a stale closure.
  const emitSelectionChange = useEvent((next: SelectionModel) => selection?.onSelectionChange(next))
  const handleToggleRow = useEvent((id: string, next: boolean) => {
    if (selectionModel === undefined) {
      return
    }
    emitSelectionChange(toggleRow(selectionModel, id, next))
  })
  const handleToggleAll = useEvent((next: boolean) => {
    // DataGridPro parity: a header click with ANY selection clears it. The indeterminate
    // header reports `checked: false`, so trusting `next` alone would read that click as
    // select-all and grow a partial selection instead of clearing it (the e2e specs pin
    // the clearing behavior on both grids). Derived from header STATE, not
    // `selectedCount` — the count is structurally 0 in `allMatching` mode until
    // `serverTotal` resolves, which would reopen the grow-from-partial hole there.
    if (!next || derivedSelection.header.checked || derivedSelection.header.indeterminate) {
      emitSelectionChange(EMPTY_SELECTION)

      return
    }
    // Default: header select-all = loaded rows only (inclusion). `allMatching` scope selects
    // the whole filtered set directly (exclusion) for filter-scoped bulk actions.
    emitSelectionChange(
      selection?.selectAllScope === 'allMatching'
        ? selectAllMatching()
        : selectAllLoaded(getLoadedRowIds(table)),
    )
  })
  const handleSelectAllMatching = useEvent(() => emitSelectionChange(selectAllMatching()))
  const handleClearSelection = useEvent(() => emitSelectionChange(EMPTY_SELECTION))

  return {
    selectionPinnedStyle,
    selectionPinnedHeaderStyle,
    selectionCellClassName,
    selectionHeaderClassName,
    derivedSelection,
    handleToggleRow,
    handleToggleAll,
    handleSelectAllMatching,
    handleClearSelection,
  }
}
